/**
 * Utility to set up token listener outside of React context
 * For use in initialization or non-React environments
 */ import { setAmplitudeUserIdentity } from './identity';
import { capturePageAction, ActionSeverity } from '@chegg/observability';
let tokenListenerCleanup = null;
/**
 * Set up automatic token listener for Amplitude identity updates.
 * This function can be called outside of React components.
 *
 * @param options Configuration options
 * @returns Promise that resolves when setup is complete
 */ export async function setupAmplitudeTokenListener(options = {}) {
    const { enabled = true, onError } = options;
    if (!enabled || typeof window === 'undefined') {
        return;
    }
    // Clean up any existing listener
    if (tokenListenerCleanup) {
        tokenListenerCleanup();
        tokenListenerCleanup = null;
    }
    try {
        // Try to import one-auth-sdk (optional dependency)
        const sdk = await import('@chegg/one-auth-sdk');
        // Check if SDK has the required methods
        if (typeof sdk.listen !== 'function' || typeof sdk.removeListener !== 'function') {
            throw new Error('one-auth-sdk does not have listen/removeListener methods');
        }
        const handleTokenChange = (payload)=>{
            const tokens = {
                accessToken: payload?.accessToken,
                idToken: payload?.idToken
            };
            if (tokens.accessToken || tokens.idToken) {
                try {
                    console.debug('AmplitudeAnalytics: Updating identity from token change');
                    setAmplitudeUserIdentity();
                } catch (error) {
                    console.error('AmplitudeAnalytics: Error updating identity from token change:', error);
                    capturePageAction({
                        action: 'Error updating amplitude identity from token change',
                        severity: ActionSeverity.WARNING,
                        location: 'amplitude-analytics-token-listener'
                    }, {
                        error: error instanceof Error ? error.message : String(error)
                    });
                    onError?.(error instanceof Error ? error : new Error('Token processing failed'));
                }
            }
        };
        const handleLogout = ()=>{
            try {
                console.debug('AmplitudeAnalytics: Updating identity from logout');
                setAmplitudeUserIdentity();
            } catch (error) {
                console.error('AmplitudeAnalytics: Error updating identity from logout:', error);
                onError?.(error instanceof Error ? error : new Error('Logout processing failed'));
            }
        };
        // Set up listeners
        sdk.listen('tokens', handleTokenChange);
        sdk.listen('logout.success', handleLogout);
        // Store cleanup function
        tokenListenerCleanup = ()=>{
            sdk.removeListener('tokens', handleTokenChange);
            sdk.removeListener('logout.success', handleLogout);
        };
        console.debug('AmplitudeAnalytics: Token listener setup complete - automatic subscription updates enabled');
    } catch (error) {
        console.debug('AmplitudeAnalytics: one-auth-sdk not available, automatic token updates disabled:', error instanceof Error ? error.message : String(error));
    // Don't call onError for missing optional dependency
    }
}
/**
 * Clean up the token listener
 */ export function cleanupAmplitudeTokenListener() {
    if (tokenListenerCleanup) {
        console.debug('AmplitudeAnalytics: Cleaning up token listener');
        tokenListenerCleanup();
        tokenListenerCleanup = null;
    }
}
/**
 * Check if token listener is active
 */ export function isTokenListenerActive() {
    return tokenListenerCleanup !== null;
}
